import {
    AppClockService,
    DateTimeService,
    DateTimeUtils,
    ISOFormat,
    WallClockDateService,
} from '@mews-framework/datetime';
import { DateTimeLocalizationInfo } from '@mews-framework/globalization';
import { CustomErrorParams, CustomErrorType } from '../../CustomErrorType';
import { MIN_BIRTH_DATE_STRING } from '../constants';
import { createDateStringSchema, dateIsoStringSchema } from './date';
import { dateTimeUtcStringSchema } from './datetime';

const validateDateRange = (minDate: string, maxDate: string) => (date: string) => date >= minDate && date <= maxDate;

const createBirthDateMessageParams = (dateTimeLocalizationInfo: DateTimeLocalizationInfo): CustomErrorParams => ({
    errorType: CustomErrorType.BirthDateString,
    minBirthDate: WallClockDateService.toString(
        WallClockDateService.parse(MIN_BIRTH_DATE_STRING),
        dateTimeLocalizationInfo
    ),
});

const wallClock = (dateTimeLocalizationInfo: DateTimeLocalizationInfo) =>
    createDateStringSchema(dateTimeLocalizationInfo).refine(
        (value) => {
            const { value: parsedValue } = WallClockDateService.fromString(value, dateTimeLocalizationInfo);
            const { value: today } = WallClockDateService.today();

            return validateDateRange(MIN_BIRTH_DATE_STRING, today)(parsedValue);
        },
        {
            params: createBirthDateMessageParams(dateTimeLocalizationInfo) satisfies CustomErrorParams,
        }
    );

const utc = ({
    dateTimeLocalizationInfo,
    timezone,
    appClockService,
}: {
    dateTimeLocalizationInfo: DateTimeLocalizationInfo;
    timezone: string;
    appClockService: AppClockService;
}) =>
    dateTimeUtcStringSchema.refine(
        (value) => {
            const { value: parsedValue } = DateTimeUtils.dateTimeUtcStringToDate(value, timezone);
            const { value: today } = DateTimeService.startOfDay(
                DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone)
            );

            return validateDateRange(MIN_BIRTH_DATE_STRING, today)(parsedValue);
        },
        {
            params: createBirthDateMessageParams(dateTimeLocalizationInfo) satisfies CustomErrorParams,
        }
    );

const iso = (dateTimeLocalizationInfo: DateTimeLocalizationInfo) =>
    dateIsoStringSchema.refine(
        (value) => {
            const { value: parsedValue } = WallClockDateService.fromString(value, ISOFormat.Date);
            const { value: today } = WallClockDateService.today();

            return validateDateRange(MIN_BIRTH_DATE_STRING, today)(parsedValue);
        },
        {
            params: createBirthDateMessageParams(dateTimeLocalizationInfo) satisfies CustomErrorParams,
        }
    );

export const birthDateSchema = {
    iso,
    utc,
    wallClock,
};
